/**
 * Onboarding State Management
 *
 * Jotai atoms for managing onboarding flow state.
 * These atoms are used throughout the onboarding flow to:
 * - Track current step progress
 * - Store collected user data
 * - Manage loading/error states
 * - Coordinate with backend persistence
 */

import { atom } from 'jotai';
import type {
  OnboardingData,
  OnboardingStep,
  UserSegment,
  MonthlyPropertyVolume,
  PrimaryPainPoint,
  AcquisitionChannel,
} from '../lib/supabase';
import { profileAtom, subscriptionUsageAtom } from './authAtoms';

// ============================================
// Core Onboarding State
// ============================================

/**
 * Current step in the onboarding flow
 * Used for routing and progress indication
 */
export const currentOnboardingStepAtom = atom<OnboardingStep>('welcome');

/**
 * Accumulated onboarding data
 * This is the source of truth for all data collected during onboarding
 * Persisted to profile.onboarding_data on each step completion
 */
export const onboardingDataAtom = atom<OnboardingData>({
  session_id: crypto.randomUUID(),
  started_at: new Date().toISOString(),
  steps_completed: [],
});

/**
 * Loading state for onboarding operations (data fetching, saving, staging)
 */
export const onboardingLoadingAtom = atom<boolean>(false);

/**
 * Error state for onboarding operations
 */
export const onboardingErrorAtom = atom<string | null>(null);

/**
 * Whether the staging operation is in progress
 * Separate from general loading to allow UI differentiation
 */
export const stagingInProgressAtom = atom<boolean>(false);

/**
 * Staging result after first staging completes
 */
export interface StagingResult {
  originalUrl: string;
  stagedUrl: string;
  style: string;
  roomType: string;
  processingTimeMs: number;
  imageId: string;
  projectId: string;
}

export const stagingResultAtom = atom<StagingResult | null>(null);

// ============================================
// Derived Atoms
// ============================================

/**
 * Whether user has an active subscription
 * Used to determine final step: ValueStep (free) vs SuccessStep (paid)
 */
export const hasActiveSubscriptionAtom = atom<boolean>((get) => {
  const subscriptionUsage = get(subscriptionUsageAtom);
  // If we have subscription usage data with credits, user has a subscription
  return (
    subscriptionUsage !== null &&
    subscriptionUsage.subscription?.status === 'active'
  );
});

/**
 * Whether user is a "pro" segment (agent, photographer, brokerage)
 * Used to determine question flow
 */
export const isProSegmentAtom = atom<boolean>((get) => {
  const data = get(onboardingDataAtom);
  const profile = get(profileAtom);
  const segment = data.session_id ? profile?.user_segment : null;
  return segment === 'agent' || segment === 'photographer' || segment === 'brokerage';
});

/**
 * Current step progress for progress indicator
 * Returns { current: number, total: number, percentage: number }
 */
export const stepProgressAtom = atom((get) => {
  const currentStep = get(currentOnboardingStepAtom);
  const hasSubscription = get(hasActiveSubscriptionAtom);

  // Define step order (excluding conditional final steps)
  const baseSteps: OnboardingStep[] = ['welcome', 'questions', 'stage', 'result', 'personalize'];
  const finalStep: OnboardingStep = hasSubscription ? 'success' : 'value';
  const allSteps = [...baseSteps, finalStep];

  const currentIndex = allSteps.indexOf(currentStep);
  const total = allSteps.length;
  const current = currentIndex + 1;
  const percentage = Math.round((current / total) * 100);

  return { current, total, percentage, steps: allSteps };
});

/**
 * Selected user segment (from collected data or profile)
 */
export const selectedSegmentAtom = atom<UserSegment | null>((get) => {
  const profile = get(profileAtom);
  return profile?.user_segment ?? null;
});

/**
 * Whether onboarding can be skipped (only after first staging is complete)
 */
export const canSkipOnboardingAtom = atom<boolean>((get) => {
  const stagingResult = get(stagingResultAtom);
  return stagingResult !== null;
});

/**
 * Computed time-to-first-stage in seconds
 */
export const timeToFirstStageAtom = atom<number | null>((get) => {
  const data = get(onboardingDataAtom);
  if (!data.started_at || !data.staging_completed_at) return null;

  const startTime = new Date(data.started_at).getTime();
  const endTime = new Date(data.staging_completed_at).getTime();
  return Math.round((endTime - startTime) / 1000);
});

// ============================================
// Action Atoms (Writable)
// ============================================

/**
 * Update onboarding data with new values
 * Merges new data with existing data
 */
export const updateOnboardingDataAtom = atom(
  null,
  (get, set, update: Partial<OnboardingData>) => {
    const currentData = get(onboardingDataAtom);
    set(onboardingDataAtom, {
      ...currentData,
      ...update,
    });
  }
);

/**
 * Mark a step as completed
 * Adds step to steps_completed array and updates last_completed_step
 */
export const completeStepAtom = atom(
  null,
  (get, set, step: OnboardingStep) => {
    const currentData = get(onboardingDataAtom);
    const stepsCompleted = currentData.steps_completed || [];

    // Only add if not already completed
    if (!stepsCompleted.includes(step)) {
      set(onboardingDataAtom, {
        ...currentData,
        steps_completed: [...stepsCompleted, step],
        last_completed_step: step,
      });
    }
  }
);

/**
 * Set staging result and update onboarding data
 */
export const setStagingResultAtom = atom(
  null,
  (get, set, result: StagingResult) => {
    set(stagingResultAtom, result);
    set(stagingInProgressAtom, false);

    // Update onboarding data with staging info
    const currentData = get(onboardingDataAtom);
    const startTime = currentData.started_at
      ? new Date(currentData.started_at).getTime()
      : Date.now();
    const completionTime = Date.now();
    const timeToFirstStageSeconds = Math.round((completionTime - startTime) / 1000);

    set(onboardingDataAtom, {
      ...currentData,
      staging_completed_at: new Date().toISOString(),
      time_to_first_stage_seconds: timeToFirstStageSeconds,
      first_style_selected: result.style,
      first_room_type_staged: result.roomType,
      first_staged_image_id: result.imageId,
      first_project_id: result.projectId,
    });
  }
);

/**
 * Reset onboarding state (for testing or retry scenarios)
 */
export const resetOnboardingAtom = atom(null, (_get, set) => {
  set(currentOnboardingStepAtom, 'welcome');
  set(onboardingDataAtom, {
    session_id: crypto.randomUUID(),
    started_at: new Date().toISOString(),
    steps_completed: [],
  });
  set(onboardingLoadingAtom, false);
  set(onboardingErrorAtom, null);
  set(stagingInProgressAtom, false);
  set(stagingResultAtom, null);
});

// ============================================
// Segment-Specific Atoms
// ============================================

/**
 * Pro user questions data
 */
export const proQuestionsDataAtom = atom<{
  monthlyVolume: MonthlyPropertyVolume | null;
  painPoint: PrimaryPainPoint | null;
}>((get) => {
  const data = get(onboardingDataAtom);
  return {
    monthlyVolume: data.monthly_property_volume ?? null,
    painPoint: data.primary_pain_point ?? null,
  };
});

/**
 * Personalization data
 */
export const personalizationDataAtom = atom<{
  mlsMarket: string | null;
  brokerageName: string | null;
  acquisitionChannel: AcquisitionChannel | null;
}>((get) => {
  const data = get(onboardingDataAtom);
  return {
    mlsMarket: data.mls_market ?? null,
    brokerageName: data.brokerage_name ?? null,
    acquisitionChannel: data.acquisition_channel ?? null,
  };
});

// ============================================
// ROI Calculator Atoms
// ============================================

/** Default traditional staging cost per property */
export const TRADITIONAL_STAGING_COST = 3000;

/** Plan costs for ROI calculation */
export const PLAN_COSTS = {
  starter: { monthly: 19, credits: 60 },
  professional: { monthly: 39, credits: 180 },
  premium: { monthly: 79, credits: 400 },
} as const;

/**
 * ROI calculation based on property volume
 */
export const roiCalculationAtom = atom((get) => {
  const data = get(onboardingDataAtom);

  // Parse volume to number (use midpoint of range)
  const volumeMap: Record<MonthlyPropertyVolume, number> = {
    '1-3': 2,
    '4-10': 7,
    '11-25': 18,
    '26+': 30,
  };

  const volume = data.monthly_property_volume
    ? volumeMap[data.monthly_property_volume]
    : 5; // Default to 5 if not set

  const traditionalCost = volume * TRADITIONAL_STAGING_COST;

  // Recommend plan based on volume
  const recommendedPlan =
    volume <= 3 ? 'starter' : volume <= 10 ? 'professional' : 'premium';

  const planCost = PLAN_COSTS[recommendedPlan].monthly;
  const monthlySavings = traditionalCost - planCost;
  const annualSavings = monthlySavings * 12;
  const savingsPercentage = Math.round((monthlySavings / traditionalCost) * 100);

  return {
    propertiesPerMonth: volume,
    traditionalCost,
    vspCost: planCost,
    monthlySavings,
    annualSavings,
    savingsPercentage,
    recommendedPlan,
  };
});
